import { useState } from 'react'
import { GraphFormatError, parseGraph } from '@rk/contracts/graph'
import type { Graph } from './graph.js'

export interface ImportExportProps {
  graph: Graph
  /** Отдать содержимое файла наружу: скачиванием занимается вызывающий. */
  onDownload?: (content: string, fileName: string) => void
  onImported?: (graph: Graph) => void
  fileName?: string
}

export const DEFAULT_FILE_NAME = 'pipeline.json'

/**
 * Перенос сценария файлом (FR-UI-008). Отступы в выгрузке обязательны: JSON
 * в одну строку нельзя ни прочитать, ни осмысленно сравнить в системе
 * контроля версий, а именно туда его и кладут.
 *
 * Загрузка проверяется тем же `parseGraph`, что и публикация: своя проверка
 * приняла бы граф, который служба отвергнет, — и человек узнал бы об этом
 * при публикации, потеряв работу по правкам поверх испорченного графа.
 */
export function ImportExport({
  graph,
  onDownload,
  onImported,
  fileName = DEFAULT_FILE_NAME,
}: ImportExportProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null)

  const accept = async (file: File): Promise<void> => {
    let parsed: unknown
    try {
      parsed = JSON.parse(await file.text())
    } catch {
      // Отличается от «не тот формат» намеренно: битый файл и правильный
      // файл не той формы лечатся по-разному.
      setError('Файл не разбирается как JSON. Возможно, он повреждён или это не тот файл.')
      return
    }

    try {
      const imported = parseGraph(parsed)
      setError(null)
      onImported?.(imported)
    } catch (failure: unknown) {
      setError(
        failure instanceof GraphFormatError
          ? failure.message
          : 'Файл не соответствует формату графа.',
      )
    }
  }

  return (
    <div className="перенос">
      <button
        type="button"
        className="btn"
        onClick={() => {
          onDownload?.(JSON.stringify(graph, null, 2), fileName)
        }}
      >
        Скачать файл
      </button>

      {/*
        Поле выбора файла показывается своей подписью, а само остаётся
        скрытым от глаз, но не от программ: собственный вид у него
        системный и по-английски («Choose File»), а прятать его через
        display:none значит убрать его и с клавиатуры, и из чтения
        экранным диктором.

        Подпись оформлена обычной кнопкой, а не призрачной (FR-ADM-039):
        призрачная — без рамки и без заливки — между двумя настоящими
        кнопками читалась голым текстом, то есть забытой вёрсткой. Роль
        у действия та же, что у соседей, значит и вид тот же.
      */}
      <label htmlFor="перенос-файл" className="btn">
        Загрузить из файла
      </label>
      <input
        id="перенос-файл"
        className="визуально-скрыто"
        type="file"
        accept="application/json,.json"
        onChange={(event) => {
          const file = event.target.files?.[0]
          if (file === undefined) return
          void accept(file)
        }}
      />

      {error !== null ? (
        <p className="перенос__отказ" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
